/**
 * ProjectConnectionsManager — admin view listing EVERY saved DB connection
 * for a project, across both kinds (source/target), regardless of which
 * agent created it. Rename, delete, or add a new one from here.
 *
 * Complements (doesn't replace) SharedDBConfig above it in ProjectSetup:
 * SharedDBConfig is the legacy single-slot source/target config; this is
 * the full list any agent's "connection saved by another agent" picker
 * (ConnectDatabaseDialog) reads from — see
 * admin/project_db_config.list_connections_for_project.
 */

import { useCallback, useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Database,
  Loader2,
  Pencil,
  Plus,
  Server,
  Trash2,
  X,
} from "lucide-react";
import { apiv2, type DBConfigOut } from "../apiv2";
import {
  ConnectDatabaseDialog,
  type ConnectResult,
  type DbConnectFormValues,
} from "./data-source/ConnectDatabaseDialog";

interface Props {
  projectId: string;
  canEdit: boolean;
}

function ConnectionRow({
  conn,
  canEdit,
  onRename,
  onDelete,
}: {
  conn: DBConfigOut;
  canEdit: boolean;
  onRename: (name: string) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState(conn.name || "");
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const submitRename = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === conn.name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onRename(trimmed);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
      <Database size={14} className="text-white/40 shrink-0" />
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitRename();
                if (e.key === "Escape") setEditing(false);
              }}
              className="px-2 py-1 rounded-md bg-white/5 border border-white/10 text-sm text-white flex-1"
            />
            <button
              onClick={submitRename}
              disabled={saving}
              className="text-[11px] text-brand-400 hover:text-brand-300 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="text-white/30 hover:text-white/50"
            >
              <X size={13} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white truncate">
              {conn.name || conn.db_type}
            </span>
            {canEdit && (
              <button
                onClick={() => {
                  setNameDraft(conn.name || "");
                  setEditing(true);
                }}
                className="text-white/20 hover:text-white/50 transition-colors"
                title="Rename"
              >
                <Pencil size={11} />
              </button>
            )}
          </div>
        )}
        <div className="text-[11px] text-white/40 mt-0.5 truncate">
          {conn.db_type} · {conn.host}
          {conn.database ? `/${conn.database}` : ""}
          {conn.schema_name ? ` · ${conn.schema_name}` : ""}
          {conn.updated_at
            ? ` · updated ${new Date(conn.updated_at).toLocaleDateString()}`
            : ""}
        </div>
      </div>
      {canEdit && (
        <>
          {confirmingDelete ? (
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[11px] text-white/50">Delete?</span>
              <button
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await onDelete();
                  } finally {
                    setDeleting(false);
                    setConfirmingDelete(false);
                  }
                }}
                disabled={deleting}
                className="text-[11px] text-red-400 hover:text-red-300 disabled:opacity-50"
              >
                {deleting ? "…" : "Confirm"}
              </button>
              <button
                onClick={() => setConfirmingDelete(false)}
                className="text-[11px] text-white/40 hover:text-white/60"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmingDelete(true)}
              className="text-white/20 hover:text-red-400 transition-colors shrink-0"
              title="Delete connection"
            >
              <Trash2 size={14} />
            </button>
          )}
        </>
      )}
    </div>
  );
}

function ConnectionKindSection({
  projectId,
  kind,
  canEdit,
}: {
  projectId: string;
  kind: "source" | "target";
  canEdit: boolean;
}) {
  const [connections, setConnections] = useState<DBConfigOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list =
        kind === "source"
          ? await apiv2.listSourceConnections(projectId)
          : await apiv2.listTargetConnections(projectId);
      setConnections(list);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [projectId, kind]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRename = async (connectionId: string, name: string) => {
    if (kind === "source") {
      await apiv2.renameSourceConnection(projectId, connectionId, name);
    } else {
      await apiv2.renameTargetConnection(projectId, connectionId, name);
    }
    await load();
  };

  const handleDelete = async (connectionId: string) => {
    if (kind === "source") {
      await apiv2.deleteSourceConnection(projectId, connectionId);
    } else {
      await apiv2.deleteTargetConnection(projectId, connectionId);
    }
    await load();
  };

  const handleAddNew = async (
    values: DbConnectFormValues,
  ): Promise<ConnectResult> => {
    try {
      const body = {
        db_type: values.db_type,
        host: values.host,
        port: parseInt(values.port, 10),
        database: values.database,
        schema_name: values.schema_name,
        username: values.username,
        password: values.password,
        account: values.account,
        warehouse: values.warehouse,
        role: values.role,
      };
      if (kind === "source") {
        await apiv2.createSourceConnection(projectId, body);
      } else {
        await apiv2.createTargetConnection(projectId, body);
      }
      await load();
      return { ok: true };
    } catch (err) {
      return {
        ok: false,
        error: err instanceof Error ? err.message : "Failed to add connection",
      };
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-widest text-white/40">
          {kind === "source" ? "Source Connections" : "Target Connections"}
        </span>
        {canEdit && (
          <button
            onClick={() => setShowAddDialog(true)}
            className="flex items-center gap-1 text-[11px] text-brand-400 hover:text-brand-300 transition-colors"
          >
            <Plus size={12} />
            Add connection
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 size={18} className="animate-spin text-white/20" />
        </div>
      ) : error ? (
        <p className="text-xs text-red-400">{error}</p>
      ) : connections.length === 0 ? (
        <p className="text-xs text-white/30 py-2">
          No {kind} connections saved yet for this project.
        </p>
      ) : (
        <div className="space-y-2">
          {connections.map((conn) => (
            <ConnectionRow
              key={conn.id}
              conn={conn}
              canEdit={canEdit}
              onRename={(name) => handleRename(conn.id, name)}
              onDelete={() => handleDelete(conn.id)}
            />
          ))}
        </div>
      )}

      <ConnectDatabaseDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        title={`Add ${kind === "source" ? "Source" : "Target"} Connection`}
        projectId={projectId}
        kind={kind}
        startOnNewForm
        onConnectExisting={async () => ({
          ok: false,
          error: "not applicable",
        })}
        onConnectNew={handleAddNew}
      />
    </div>
  );
}

export default function ProjectConnectionsManager({
  projectId,
  canEdit,
}: Props) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="glass rounded-2xl overflow-hidden"
      style={{
        background: "rgba(28,28,42,0.5)",
        border: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/[0.02] transition"
      >
        <Server size={16} className="text-brand-400" />
        <span className="text-sm font-bold text-white flex-1">
          Saved Connections
        </span>
        <span className="text-[11px] text-white/30">
          Shared across every agent
        </span>
        {open ? (
          <ChevronUp size={14} className="text-white/30" />
        ) : (
          <ChevronDown size={14} className="text-white/30" />
        )}
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 space-y-5">
          <ConnectionKindSection
            projectId={projectId}
            kind="source"
            canEdit={canEdit}
          />
          <ConnectionKindSection
            projectId={projectId}
            kind="target"
            canEdit={canEdit}
          />
        </div>
      )}
    </div>
  );
}
